/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiSpacer } from '@elastic/eui'

import type { ClusterPlanStepInfo } from '@modules/cloud-api/v1/types'
import type { StackDeployment, AnyClusterPlanInfo } from '@modules/ui-types'

import { isPendingAttempt } from '@/lib/stackDeployments/selectors/configurationChangeAttempts'

import ShardDataMigrationTable from '../ShardDataMigrationTable'

import type { FunctionComponent } from 'react'

type Props = {
  deployment: StackDeployment
  planAttempt: AnyClusterPlanInfo
  spacerBefore?: boolean
  spacerAfter?: boolean
}

const StackConfigurationChangeStepDataMigration: FunctionComponent<Props> = ({
  deployment,
  planAttempt,
  spacerBefore,
  spacerAfter,
}) => {
  if (!isPendingAttempt({ planAttempt })) {
    // sanity check to guard against API bugs
    return null
  }

  const { plan_attempt_log } = planAttempt
  const lastMessage = plan_attempt_log[plan_attempt_log.length - 1]

  if (!lastMessage) {
    return null
  }

  const dataMigration = isDataMigrationStep(lastMessage)

  if (!dataMigration) {
    return null
  }

  return (
    <Fragment>
      {spacerBefore && <EuiSpacer size='m' />}

      <ShardDataMigrationTable deployment={deployment} />

      {spacerAfter && <EuiSpacer size='m' />}
    </Fragment>
  )
}

function isDataMigrationStep(step: ClusterPlanStepInfo): boolean {
  // eslint-disable-next-line lodash/prefer-matches
  const isAMigrationStep =
    step.step_id === `migrate-data` || step.step_id === `shutdown-elasticsearch-nodes`
  return isAMigrationStep && step.stage !== `completed` && step.status === `pending`
}

export default StackConfigurationChangeStepDataMigration
